import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Supplier } from "../models/Supplier";
import {
	removeSupplier,
	upsertSupplier,
} from "../repositories/SuppliersRepository";
import { SUPPLIERS_QUERY_KEYS } from "./useSuppliers";

interface MutationOptions {
	onSuccess?: (data?: Supplier) => void;
	onError?: (error: Error) => void;
}

export const useUpsertSupplier = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (supplier: Supplier) => {
			return await upsertSupplier(supplier);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: SUPPLIERS_QUERY_KEYS.all,
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertSupplier: mutation.mutate,
		upsertSupplierAsync: mutation.mutateAsync,
		isUpserting: mutation.isPending,
	};
};

export const useDeleteSupplier = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (id: number) => {
			await removeSupplier(id);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: SUPPLIERS_QUERY_KEYS.all,
			});
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		deleteSupplier: mutation.mutate,
		deleteSupplierAsync: mutation.mutateAsync,
		isDeleting: mutation.isPending,
	};
};
